/* =============================================================
   SPENDIQ — Mock Data (simulates real backend API responses)
   ============================================================= */

// ── KPI Cards ───────────────────────────────────────────────
export const kpiData = [
  { id: "totalSpend", label: "Total Spend", icon: "DollarSign", value: "$2.84B", raw: 248.6, trend: "+4.2%", up: true, note: "Indirect spend rose 6.1% driven by Professional Services growth in EMEA."},
  { id: "spendUnderManagement", label: "Spend Under Management", icon: "TrendingUp", value: "$78.4M", raw: 198.2, trend: "+3.4%", up: true, note: "12 net-new categories onboarded this quarter lifted coverage."},
  { id: "classifiedSpend", label: "Classified Spend %", icon: "Users", value: "94.2%", raw: 87, trend: "+1.8%", up: true, note: "Classification confidence holding above 94% for 3rd consecutive month."},
  { id: "savingsIdentified", label: "Savings Identified", icon: "TrendingUp", value: "$18.4M", raw: 34.2, trend: "+12%", up: true, note: "Supplier fragmentation increased in APAC unlocking consolidation opportunities."},
  { id: "maverickSpend", label: "Maverick Spend", icon: "AlertTriangle", value: "$112M", raw: 12.7, trend: "+8.4%", up: false, note: "Off-contract IT purchasing dropped after procurement policy refresh."},
  { id: "supplierCount", label: "Supplier Count", icon: "Users", value: "12481", raw: 1247, trend: "-214%", up: false, note: "Consolidation reduced tail suppliers by 1.7% across MRO and Facilities."},
  { id: "strategicSupplierDependency", label: "Strategic Supplier Dependency", icon: "AlertTriangle", value: "31%", raw: 41, trend: "+2.4%", up: true, note: "Top-10 suppliers now drive 31% of addressable spend concentration risk rising."},
  { id: "aiConfidenceScore", label: "AI Confidence Score", icon: "TrendingUp", value: "92.6%", raw: 92, trend: "+0.9%", up: true, note: "Model recalibrated on 4.2M cleaned transactions from FY25 audit."},
];

// ── Spend Trend (12 months) ──────────────────────────────────
export const spendTrendData = [
  { month: "Jan", managed: 185, savings: 22, budget: 200 },
  { month: "Feb", managed: 198, savings: 24, budget: 200 },
  { month: "Mar", managed: 172, savings: 19, budget: 200 },
  { month: "Apr", managed: 210, savings: 28, budget: 210 },
  { month: "May", managed: 225, savings: 31, budget: 210 },
  { month: "Jun", managed: 208, savings: 26, budget: 220 },
  { month: "Jul", managed: 238, savings: 33, budget: 220 },
  { month: "Aug", managed: 219, savings: 29, budget: 230 },
  { month: "Sep", managed: 202, savings: 25, budget: 230 },
  { month: "Oct", managed: 241, savings: 35, budget: 240 },
  { month: "Nov", managed: 228, savings: 30, budget: 240 },
  { month: "Dec", managed: 248, savings: 34, budget: 250 },
];

// ── Category Treemap ─────────────────────────────────────────
export const categoryData = [
  { name: "IT Services",         value: 82.4, color: "#5a3d9a" },
  { name: "Facilities",           value: 44.1, color: "#7c5cbf" },
  { name: "Prof. Services",       value: 38.7, color: "#3d2d6b" },
  { name: "Logistics",            value: 31.2, color: "#9575d0" },
  { name: "MRO",                  value: 28.8, color: "#6b46b0" },
  { name: "Marketing",            value: 12.4, color: "#4e3585" },
  { name: "HR Services",          value: 7.6,  color: "#8162c2" },
  { name: "Other",                value: 3.4,  color: "#5e448f" },
];

// ── Region Data ──────────────────────────────────────────────
export const regionData = [
  { region: "North America", spend: "$98.4M", raw: 98.4, pct: 40, risk: "Low",    riskCls: "risk--low" },
  { region: "EMEA",          spend: "$74.2M", raw: 74.2, pct: 30, risk: "Medium", riskCls: "risk--medium" },
  { region: "APAC",          spend: "$52.1M", raw: 52.1, pct: 21, risk: "Medium", riskCls: "risk--medium" },
  { region: "LATAM",         spend: "$23.9M", raw: 23.9, pct: 10, risk: "High",   riskCls: "risk--high" },
];

// ── Procurement Health ───────────────────────────────────────
export const healthData = {
  score: 78,
  label: "Good",
  breakdown: [
    { label: "Process Efficiency", value: 82, color: "#6b3fa0" },
    { label: "Supplier Quality",   value: 74, color: "#983683" },
    { label: "Cost Control",       value: 79, color: "#3265aa" },
    { label: "Compliance",         value: 85, color: "#10b981" },
  ],
};

// ── AI Insights ──────────────────────────────────────────────
export const insightData = [
  {
    id: "ins-1",
    category: "Supplier Consolidation",
    severity: "High" as const,
    sevCls: "severity--high",
    confidence: 94,
    title: "Consolidate 38 logistics suppliers in EMEA",
    body: "38 suppliers serve overlapping freight lanes; top-5 cover 71% of volume.",
    amount: "$4.2M",
    amountRaw: 4.2,
    type: "savings",
    actions: ["View Analysis", "Create Consolidation Plan"],
    date: "2026-05-24",
  },
  {
    id: "ins-2",
    category: "Tail Spend",
    severity: "Critical" as const,
    sevCls: "severity--critical",
    confidence: 88,
    title: "Reduce tail spend in MRO category",
    body: "1,214 suppliers represent <5% of MRO spend with redundant SKUs.",
    amount: "$2.8M",
    amountRaw: 18.4,
    type: "risk",
    actions: ["View Contracts", "Initiate Renewal"],
    date: "2026-05-23",
  },
  {
    id: "ins-3",
    category: "Contract Leakage",
    severity: "Critical" as const,
    sevCls: "severity--critical",
    confidence: 91,
    title: "Off-contract IT purchases up 14%",
    body: "Maverick spend with 6 SaaS vendors despite negotiated MSAs.",
    amount: "",
    amountRaw: 1.8,
    type: "savings",
    actions: ["View Variance Report", "Standardize Catalog"],
    date: "2026-05-22",
  },
  {
    id: "ins-4",
    category: "Duplicate Suppliers",
    severity: "Medium" as const,
    sevCls: "severity--medium",
    confidence: 97,
    title: "2,431 duplicate supplier records flagged",
    body: "Variant names across ERPs (e.g. \"IBM Corp\" vs \"I.B.M. Inc\").",
    amount: "",
    amountRaw: 22.1,
    type: "compliance",
    actions: ["View Supplier Scores", "Send Remediation Request"],
    date: "2026-05-21",
  },
];

// ── Priority Actions ─────────────────────────────────────────
export const priorityActions = [
  { id: "pa-1", title: "Renew Acme Corp contract",          meta: "Expires in 12 days · $8.2M",                severity: "critical", daysLeft: 12 },
  { id: "pa-2", title: "Consolidate IT software vendors",   meta: "3 overlapping vendors · $4.2M savings",      severity: "high",     daysLeft: 30 },
  { id: "pa-3", title: "Review LATAM supplier ESG scores",  meta: "8 suppliers below threshold",                severity: "medium",   daysLeft: 45 },
  { id: "pa-4", title: "Approve Q1 MRO blanket order",      meta: "Pending approval · $1.4M",                   severity: "low",      daysLeft: 60 },
];

// ── Opportunity Insights ─────────────────────────────────────
export const opportunityData = [
  {
    id: "opp-1",
    type: "Supplier Consolidation",
    severity: "High" as const,
    sevCls: "severity--high",
    confidence: 94,
    stats: [
      { label: "Potential Savings", value: "$4.2M" },
      { label: "Affected Suppliers", value: "12" },
      { label: "Timeframe", value: "90 days" },
    ],
    title: "IT Vendor Consolidation Opportunity",
    body: "Spend analysis identifies 12 overlapping IT vendors providing similar services across business units. Consolidating to 3 strategic vendors would reduce management overhead and unlock volume discounts of 18–22%.",
    saving: "Recommended Action: Issue consolidated RFP for IT software",
    ai: "AI Reasoning: Similar services identified via NLP categorization with 94% confidence. Volume discount curve analysis suggests 18–22% savings at consolidated volumes. Benchmark data from 45 comparable organizations supports this estimate.",
    status: "new",
    category: "Supplier Consolidation",
  },
  {
    id: "opp-2",
    type: "Contract Optimization",
    severity: "Critical" as const,
    sevCls: "severity--critical",
    confidence: 89,
    stats: [
      { label: "Value at Risk", value: "$18.4M" },
      { label: "Contracts", value: "3" },
      { label: "Expiry", value: "30 days" },
    ],
    title: "Critical Contract Renewals Required",
    body: "Three strategic contracts are approaching expiration without renewal initiation. These represent significant spend and operational dependencies. Auto-renewal clauses may activate at unfavorable terms if not addressed.",
    saving: "Recommended Action: Initiate emergency renewal process immediately",
    ai: "AI Reasoning: Contract timeline analysis cross-referenced with operational dependency mapping. Expiry risk scored at 0.89 based on renewal lead-time benchmarks. Similar contracts renewed late incurred an average 8.3% rate increase.",
    status: "new",
    category: "Contract Optimization",
  },
  {
    id: "opp-3",
    type: "Price Variance",
    severity: "Medium" as const,
    sevCls: "severity--medium",
    confidence: 91,
    stats: [
      { label: "Potential Savings", value: "$1.8M" },
      { label: "Items Affected", value: "847" },
      { label: "Variance", value: "23%" },
    ],
    title: "MRO Category Price Standardization",
    body: "Purchase order analysis reveals 23% price variance for identical MRO items across 14 facilities. Implementing a preferred vendor catalog with negotiated pricing could eliminate this variance and streamline procurement.",
    saving: "Recommended Action: Implement centralized MRO catalog with locked pricing",
    ai: "AI Reasoning: Item-level price comparison using product code matching across 847 unique SKUs across facilities. Statistical analysis confirms variance is not attributable to regional cost differences.",
    status: "new",
    category: "Price Variance",
  },
  {
    id: "opp-4",
    type: "ESG Compliance",
    severity: "Medium" as const,
    sevCls: "severity--medium",
    confidence: 87,
    stats: [
      { label: "Affected Spend", value: "$22.1M" },
      { label: "Suppliers", value: "8" },
      { label: "Avg Score", value: "51/100" },
    ],
    title: "ESG Supplier Remediation Program",
    body: "Eight suppliers representing $22.1M in annual spend score below the corporate ESG threshold of 65. A structured remediation program with clear timelines and exit clauses is recommended.",
    saving: "Recommended Action: Launch ESG remediation program with 90-day milestones",
    ai: "AI Reasoning: ESG scores sourced from EcoVadis and proprietary assessment model. Correlation analysis shows 12% higher contract risk for sub-65 scoring suppliers. Regulatory exposure under upcoming EU CSRD requirements flagged.",
    status: "new",
    category: "ESG Compliance",
  },
  {
    id: "opp-5",
    type: "Payment Terms",
    severity: "High" as const,
    sevCls: "severity--high",
    confidence: 92,
    stats: [
      { label: "Working Capital Impact", value: "$6.8M" },
      { label: "Suppliers Eligible", value: "34" },
      { label: "Avg Extension", value: "+18 days" },
    ],
    title: "Payment Terms Extension Opportunity",
    body: "Analysis identifies 34 financially stable suppliers where payment terms could be extended from Net-30 to Net-45 or Net-60, improving working capital without impacting supplier relationships.",
    saving: "Recommended Action: Renegotiate payment terms in upcoming contract renewals",
    ai: "AI Reasoning: Supplier financial health scored using Dun & Bradstreet data combined with payment history. Suppliers with PAYDEX >80 selected. Working capital model projects $6.8M improvement at current payment volumes.",
    status: "new",
    category: "Supplier Consolidation",
  },
  {
    id: "opp-6",
    type: "Demand Management",
    severity: "Medium" as const,
    sevCls: "severity--medium",
    confidence: 85,
    stats: [
      { label: "Potential Savings", value: "$2.3M" },
      { label: "Rogue Spend", value: "$8.9M" },
      { label: "PO Compliance", value: "68%" },
    ],
    title: "Rogue Spend Reduction in Professional Services",
    body: "32% of Professional Services spend ($8.9M) occurs outside approved vendor list and procurement process. Tightening approval workflows and pre-approved vendor panels would reduce rogue spend significantly.",
    saving: "Recommended Action: Implement pre-approved vendor panel with streamlined approval",
    ai: "AI Reasoning: Transaction pattern analysis identified 847 non-PO purchases over $5K threshold. Comparison with approved vendor rates shows average 26% premium for off-contract purchases.",
    status: "new",
    category: "Price Variance",
  },
];

// ── Filter Options ───────────────────────────────────────────
export const filterOptions = {
  categories: ["All Categories", "IT Services", "Facilities", "Professional Services", "Logistics", "MRO", "Marketing"],
  regions:    ["All Regions", "North America", "EMEA", "APAC", "LATAM"],
  timeframes: ["Q4 2025", "Q3 2025", "Q2 2025", "Q1 2025", "FY 2025", "Custom Range"],
  suppliers:  ["All Suppliers", "Top 50", "Top 100", "Strategic Suppliers", "New Suppliers"],
  riskLevels: ["All Risk Levels", "Critical", "High", "Medium", "Low"],
};

// ── Project Setup ────────────────────────────────────────────
export const businessUnits  = ["Operations", "Finance", "IT", "Marketing", "Legal", "HR", "Supply Chain"];
export const spendCategories = ["IT Services", "Facilities", "Professional Services", "Logistics", "MRO", "Marketing", "HR Services"];
export const regions         = ["North America", "EMEA", "APAC", "LATAM", "Global"];
export const timelines       = ["Q1 2026", "Q2 2026", "Q3 2026", "Q4 2026", "FY 2026"];

// ── Team Members (Step 4) ────────────────────────────────────
export const teamMembers = [
  { id: "tm-1", initials: "AR", name: "Alex Rivera",  email: "a.rivera@company.com", dept: "Procurement",   role: "Admin",  avatarCls: "user-avatar--ar" },
  { id: "tm-2", initials: "ML", name: "Maria Lopez",  email: "m.lopez@company.com",  dept: "Finance",       role: "Editor", avatarCls: "user-avatar--ml" },
  { id: "tm-3", initials: "SW", name: "Sam Wang",     email: "s.wang@company.com",   dept: "IT Operations", role: "Viewer", avatarCls: "user-avatar--sw" },
];

// ── Notifications ────────────────────────────────────────────
export const notifications = [
  { id: "n-1", title: "3 contracts expiring soon",       time: "2 hours ago",  read: false, severity: "critical" },
  { id: "n-2", title: "New AI insight available",        time: "4 hours ago",  read: false, severity: "info" },
  { id: "n-3", title: "Q4 budget threshold reached",     time: "Yesterday",    read: true,  severity: "warning" },
  { id: "n-4", title: "Supplier risk score updated",     time: "2 days ago",   read: true,  severity: "info" },
];

// ── Opportunity Summary Metrics ──────────────────────────────
export const opportunitySummary = {
  totalValue: "$42.8M",
  highConfidence: 18,
  quickWins: "$8.4M",
  avgConfidence: 91,
};
